import { useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export interface FileUploadZoneProps {
  uploadUrl: string;
  value: string[];
  onUploaded: (paths: string[]) => void;
  onDelete: (path: string) => void;
  acceptMultiple?: boolean;
  acceptedTypes?: string;
  maxSizeBytes?: number;
  disabled?: boolean;
  dragLabel?: string;
}

function isImage(url: string): boolean {
  return /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?|$)/i.test(url);
}

function getFileName(url: string): string {
  const last = url.split('/').pop() ?? '';
  const idx = last.indexOf('-');
  return idx >= 0 ? last.slice(idx + 1) : last;
}

function UploadIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

export function FileUploadZone({
  uploadUrl,
  value,
  onUploaded,
  onDelete,
  acceptMultiple = true,
  acceptedTypes,
  maxSizeBytes = 10 * 1024 * 1024,
  disabled = false,
  dragLabel,
}: FileUploadZoneProps) {
  const { t } = useTranslation();
  const resolvedDragLabel = dragLabel ?? t('common.dragToUpload');
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const base = useMemo(
    () => (import.meta.env as Record<string, string>)['VITE_API_BASE'] ?? '',
    [],
  );

  async function handleFiles(files: File[]) {
    if (!files.length || uploading || disabled) return;
    const toUpload = acceptMultiple ? files : [files[0]!];
    const oversized = toUpload.filter(f => f.size > maxSizeBytes);
    if (oversized.length) {
      setUploadError(t('common.fileTooLarge', { size: Math.round(maxSizeBytes / 1024 / 1024) }));
      return;
    }
    setUploadError(null);
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      for (const f of toUpload) fd.append('files', f);
      const paths = await new Promise<string[]>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${base}${uploadUrl}`);
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        xhr.withCredentials = true;
        xhr.upload.onprogress = e => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve((JSON.parse(xhr.responseText) as { paths: string[] }).paths);
          } else {
            reject(new Error(`HTTP ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(fd);
      });
      onUploaded(paths);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : t('common.uploadFailed'));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }

  const circumference = 2 * Math.PI * 11;

  return (
    <div>
      {/* Drop zone */}
      <div
        onDragEnter={e => { e.preventDefault(); if (!disabled) setDragging(true); }}
        onDragOver={e => { e.preventDefault(); if (!disabled) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => {
          e.preventDefault();
          setDragging(false);
          if (!disabled) void handleFiles(Array.from(e.dataTransfer.files));
        }}
        onClick={() => { if (!disabled && !uploading) inputRef.current?.click(); }}
        style={{
          border: `1px dashed ${dragging ? 'var(--evo-cyan)' : uploadError ? 'var(--evo-danger)' : 'var(--evo-stroke)'}`,
          borderRadius: 'var(--evo-r)',
          padding: '10px var(--evo-space-3)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--evo-space-2)',
          cursor: disabled || uploading ? 'default' : 'pointer',
          background: dragging ? 'var(--evo-cyan-12)' : 'var(--evo-bg-3)',
          transition: 'background 120ms, border-color 120ms',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {uploading ? (
          <svg width={24} height={24} viewBox="0 0 24 24" style={{ flexShrink: 0, transform: 'rotate(-90deg)' }}>
            <circle cx={12} cy={12} r={11} fill="none" stroke="var(--evo-stroke)" strokeWidth={2} />
            <circle
              cx={12} cy={12} r={11} fill="none"
              stroke="var(--evo-cyan)" strokeWidth={2}
              strokeDasharray={circumference}
              strokeDashoffset={circumference * (1 - progress / 100)}
              style={{ transition: 'stroke-dashoffset 80ms linear' }}
            />
          </svg>
        ) : (
          <span style={{ color: dragging ? 'var(--evo-cyan)' : 'var(--evo-fg-4)', display: 'inline-flex', flexShrink: 0 }}>
            <UploadIcon />
          </span>
        )}
        <span style={{
          fontSize: 'var(--evo-fs-label)',
          color: uploading ? 'var(--evo-fg-3)' : uploadError ? 'var(--evo-danger)' : 'var(--evo-fg-4)',
          fontFamily: 'var(--evo-font)',
        }}>
          {uploading ? t('common.uploading', { progress }) : uploadError ? uploadError : resolvedDragLabel}
        </span>
        <input
          ref={inputRef}
          type="file"
          multiple={acceptMultiple}
          accept={acceptedTypes}
          style={{ display: 'none' }}
          disabled={disabled || uploading}
          onChange={e => {
            void handleFiles(Array.from(e.target.files ?? []));
            if (inputRef.current) inputRef.current.value = '';
          }}
        />
      </div>

      {/* Uploaded file tiles */}
      {value.length > 0 && (
        <div style={{ marginTop: 'var(--evo-space-2)', display: 'flex', flexWrap: 'wrap', gap: 'var(--evo-space-2)' }}>
          {value.map(url => {
            const img = isImage(url);
            const name = getFileName(url);
            return (
              <div key={url} style={{
                position: 'relative',
                width: 64, height: 64,
                borderRadius: 'var(--evo-r)',
                border: '1px solid var(--evo-stroke)',
                overflow: 'hidden',
                background: 'var(--evo-bg-2)',
                flexShrink: 0,
              }}>
                {img ? (
                  <img src={url} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                    <span style={{ color: 'var(--evo-fg-3)' }}><FileIcon /></span>
                    <span style={{ fontSize: '9px', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)', maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {name}
                    </span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={e => { e.stopPropagation(); onDelete(url); }}
                  style={{
                    position: 'absolute', top: 2, right: 2,
                    width: 16, height: 16, borderRadius: '50%',
                    background: 'rgba(0,0,0,0.75)', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', padding: 0, flexShrink: 0,
                  }}
                >
                  <svg width={8} height={8} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
